import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutcomes } from '../../../../api/strategy-client/strategy-client.hooks';
import { ContentPlaceholder } from '../../../../components/content-placeholder/content-placeholder';
import { Table } from '../../../../components/table/table';
import { getCurrency } from '../../../../helpers/currency-helpers';
import { useFeatureFlag } from '../../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../../helpers/feature-flags/feature-flags.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import { resetPage, setPage, setSelectedInitiative } from '../../../../store/strategy-store/strategy-store.actions';
import { useFilteredInitiatives, useFiltersApplied } from '../../strategy.hooks';
import { InitiativeRecord } from '../../strategy.type';
import { MainSectionTableGrouped } from './main-section-table-grouped';
import { createSortTableWithOutcomes, getTableColumns } from './main-section.helpers';

export const MainSectionTable = () => {
  const { t } = useTranslation();
  const page = useStrategyStore((state) => state.page);
  const tableGroupBy = useStrategyStore((state) => state.tableGroupBy);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const organization = useGlobalStore((state) => state.organization);
  const selectedInitiative = useStrategyStore((state) => state.selectedInitiative);
  const tableSortBy = useStrategyStore((state) => state.tableSortBy);
  const tableSortDirection = useStrategyStore((state) => state.tableSortDirection);

  const { initiatives } = useFilteredInitiatives();
  const filtersApplied = useFiltersApplied();
  const { outcomes } = useOutcomes();
  const showParentOutcomes = useFeatureFlag(FeatureFlag.CorporateGoals);
  const { code: currencyCode } = getCurrency(organization);

  // Create sort function with outcomes context
  const sortTableWithOutcomes = useMemo(() => createSortTableWithOutcomes(outcomes), [outcomes]);

  useEffect(() => {
    if (portfolio) {
      resetPage();
    }
  }, [portfolio]);

  if (!filtersApplied) {
    return <ContentPlaceholder noFiltersApplied />;
  }

  if (initiatives.length === 0) {
    return <ContentPlaceholder />;
  }

  if (tableGroupBy) {
    return <MainSectionTableGrouped groupBy={tableGroupBy} />;
  }

  return (
    <Table<InitiativeRecord>
      data={initiatives}
      columns={getTableColumns(t, outcomes, showParentOutcomes, currencyCode)}
      getRowId={(initiative) => initiative.id}
      defaultSortedBy={tableSortBy}
      defaultSortedByDirection={tableSortDirection}
      stickyHeader
      onRowClick={setSelectedInitiative}
      currentPage={page}
      totalItems={initiatives.length}
      onPageChange={setPage}
      customSort={sortTableWithOutcomes}
      selectedRowId={selectedInitiative?.id}
    />
  );
};
